import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
	Form, FormGroupInputControl, ErrorPage, LoadingIndicator, Alert, A,connectedComponent, clearExceptions
} from 'gwx-voyager-components';
import { updateInvitation, loadInvitation } from '../invitations.actions';
import { loadProfile } from '../../Profile/profile.actions';
import { parseAttributes } from '../../Profile/ProfileUtils';

/**
 * ClaimInvitationForm component provides a form used
 * to send a new Invitation for a desired Community.
 *
 * @class ClaimInvitationForm
 * @extends {Component}
 */
class ClaimInvitationForm extends Component {
	constructor(props) {
		super(props);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleAccept = this.handleAccept.bind(this);
		this.handleDecline = this.handleDecline.bind(this);
		this.state = { activationKey: '', containsVerifiedEmail: false, profileFetchCompleted: false };
	}

	UNSAFE_componentWillMount() {
		this.props.fetchProfile(this.props.profileId).then(response => {
			if (response.type === 'PROFILE_SUCCESS') {
				const attributes = parseAttributes(response.response.entities.profile, this.props.profileId);
				if ("com.cerner.gwx.voyager.profiles.email" in attributes) {
					this.props.loadInvitation(this.props.invitationId);
					this.setState({ containsVerifiedEmail: true });
				}
				this.setState({ profileFetchCompleted: true });
			}
		});

	}

	shouldComponentUpdate(nextProps) {
		if (JSON.stringify(nextProps.invitationId) !== JSON.stringify(this.props.invitationId)) {
			this.props.loadInvitation(nextProps.invitationId);
			return false;
		}

		return true;
	}

	/**
	 * Handles changing the value of an input.
	 *
	 * @param {Event} e
	 * 		The input change event.
	 */
	handleInputChange(e) {
		const { target } = e;
		this.setState({ [target.name]: target.value });
	}

	/**
	 * Handles accepting the Invitation
	 *
	 * @param {Event} e
	 * 		The form accept event.
	 */
	handleAccept(e) {
		e.preventDefault();
		const invitation = {
			...this.props.invitation,
			activationKey: this.state.activationKey,
			status: 2
		};
		this.props.updateInvitation(invitation)
			.then((response) => this.props.navigateToCommunity(response, invitation));
	}

	/**
	 * Handles declining the Invitation
	 *
	 * @param {Event} e
	 * 		The form decline event.
	 */
	handleDecline(e) {
		e.preventDefault();
		const invitation = { ...this.props.invitation, status: 4 };
		this.props.updateInvitation(invitation).then((response) => this.props.discard(response));
	}

	render() {
		if (this.props.isFetching) {
			return <LoadingIndicator />;
		}

		if (this.state.profileFetchCompleted === false) {
			return <LoadingIndicator />;
		}

		if (this.state.containsVerifiedEmail === false) {
			return <>
				<p className='alert alert-info lead text-center'>
					You have no verified email address, check your profile page and
					either add an email address and verify or verify an existing email address.
					<br></br>
					<A href={`/profile/${this.props.profileId}`} reactRouter>
						Profile
					</A>
				</p>
			</>
		}

		if (!this.props.isFetching && !this.props.invitation) {
			return <ErrorPage heading='Invalid Invitation' details={`Could not find Invitation with id ${this.props.invitationId}.`} />;
		}

		let alertProps = {
			visible: true
		};

		const formProps = {
			buttonsDisabled: this.state.acceptTermsofUse,
			legend: `${this.props.community.name} - Client Administrator Invitation`,
			onClickDiscard: this.handleDecline,
			onSubmit: this.handleAccept,
			primaryButtonLabel: 'Accept',
			secondaryButtonLabel: 'Decline'
		};

		switch (this.props.invitation.status) {
			case 2:
				alertProps = Object.assign(alertProps, {
					message: 'Invitation has already been accepted.',
					type: 'info'
				});
				break;
			case 4:
				alertProps = Object.assign(alertProps, {
					message: 'The invitation you are trying to retrieve has been declined.',
					type: 'warning'
				});
				break;
			case 8:
				alertProps = Object.assign(alertProps, {
					message: 'The invitation you are trying to retrieve has been revoked.',
					type: 'danger'
				});
				break;
			default:
				return (
					<Form {...formProps}>
						<FormGroupInputControl
							control={{
								id: 'activationKey',
								name: 'activationKey',
								onChange: this.handleInputChange,
								value: this.state.activationKey,
								inError: this.props.exceptions.fields && this.props.exceptions.fields.activationKey ? true : false
							}}
							helpText={this.props.exceptions.fields
								? this.props.exceptions.fields.activationKey : null}
							key='activationKey'
							label='Activation Key'
							// eslint-disable-line react/style-prop-object
						/>
						<p className='text-muted'>
							<em>
								An activation key was provided to you by your Cerner
								representative for security purposes. Please enter it above.
								If you did not receive the activation key or have misplaced it,
								please
								{' '}
								<a href='/contact'>contact us</a>
							</em>
						</p>
					</Form>
				);
		}

		return <Alert {...alertProps} />;
	}
}

ClaimInvitationForm.defaultProps = {
	invitation: null
};

ClaimInvitationForm.propTypes = {
	exceptions: PropTypes.shape({
		fields: PropTypes.object,
		timeStamp: PropTypes.number
	}).isRequired,
	updateInvitation: PropTypes.func.isRequired,
	loadInvitation: PropTypes.func.isRequired,
	loadProfile:PropTypes.func.isRequired,
	invitationId: PropTypes.string.isRequired,
	isFetching: PropTypes.bool.isRequired,
	invitation: PropTypes.shape({
		id: PropTypes.string,
		status: PropTypes.number
	}),
	discard: PropTypes.func.isRequired,
	navigateToCommunity: PropTypes.func.isRequired,
	community: PropTypes.shape({
		name: PropTypes.string,
		id: PropTypes.string
	}).isRequired
};

/**
 * Maps the current state of the Redux store to a set of
 * properties to be passed to the component.
 *
 * @param {object} state
 */
const mapStateToProps = (state, props) => {
	const {
		match: { params: { invitationId } }
	} = props;

	const {
		entities: { invitation,profile},
		exceptions
	} = state;

	return {
		invitationId,
		isFetching: invitation.isFetching,
		invitation: invitation[invitationId],
		exceptions,
		profile
	};
};

/**
 * Creates a set of functions that utilize the dispatcher to
 * dispatch actions. These are the Action Creators for the
 * InvitationsForm component.
 *
 * @param {function} dispatch
 * 		The redux dispatcher.
 * @param {object} props
 * 		The props being passed to the component.
 */
const mapDispatchToProps = (dispatch, props) => ({
	/**
	 * Discards the form.
	 */
	discard: (response) => {
		if (!response || !response.error) {
			dispatch(clearExceptions);
			props.history.push('/');
		}
	},
	navigateToCommunity: (response, invitation) => {
		if (!response || !response.error) {
			dispatch(clearExceptions);
			props.history.push(`/${invitation.communityId}/reports`);
		}
	},
	/**
	 * Makes request to update the given Invitation.
	 *
	 * @param {object} invitation
	 * 		The Invitation to update.
	 */
	updateInvitation: (invitation) => dispatch(
		updateInvitation(props.match.params.invitationId, invitation)
	),
	/**
	 * Makes request to fetch an Invitation by the given id.
	 *
	 * @param {object} id
	 * 		The invitation id.
	 */
	loadInvitation: (id) => dispatch(loadInvitation(id)),
	fetchProfile: (id) => dispatch(loadProfile(id))
});

const ClaimInvitationFormContainer = connectedComponent(ClaimInvitationForm, mapStateToProps, mapDispatchToProps);

export { ClaimInvitationFormContainer as default, ClaimInvitationForm };
